import { http } from "../utils/http";
import { relationVacancyName } from "../utils/candidateMapper";

const normalizeStatusForBackendValue = (status) => {
    if (status == null) return status;
    const s = typeof status === "object" ? (status.code ?? status.name ?? status.id) : status;
    const val = String(s).trim();
    const map = {
        Архив: "не подходит: резюме",
        "Переведен в архив": "не подходит: резюме",
        "Черный список": "не подходит: резюме",
        "Чёрный список": "не подходит: резюме",
        Нанят: "в штате",
        "Отказ. Переведен в архив": "не подходит: собес",
        "Отказ. В черном списке": "не подходит: собес",
        "Отказался от оффера. В архиве.": "отказался: оффер",
    };
    return map[val] || val;
};

const normalizeCandidateStatuses = (payload) => {
    const arr = payload?.items || [];

    return arr.map((s, i) => ({
        id: i + 1,
        code: s.status,
        name: s.label || s.status,
        stage: s.stage,
        state: s.state,
        nextAction: s.next_action,
        tone: s.tone,
        requiresDate: s.requires_date,
        allowedTransitions: Array.isArray(s.allowed_transitions) ? s.allowed_transitions : undefined,
    }));
};

export const getCandidatesStatuses = async () => {
    const raw = await http.get("/dict/status");
    return normalizeCandidateStatuses(raw);
};

export const getCandidates = (
  status = null,
  search = null,
  role_id = null,
  vacancy_id = null,
  is_perfect_candidate = undefined,
  page = 0,
  per_page = 20,
  category = null,
  opts = {}
) => {
  const {
    isNotSync, includeArchived,
    // правка №4/№14: вкладки воронки, мультистатус, анкета, ИИ, дата отклика, сортировка
    statuses, stage, ageFrom, ageTo, salaryFrom, salaryTo, city, gender, experience,
    aiScoreMin, appliedFrom, appliedTo, sortBy, sortDir,
    ...requestOptions
  } = opts || {};
  const params = new URLSearchParams();
  if (status) {
    const raw = typeof status === "object" ? (status.code ?? status.id ?? status.name) : status;
    const normalized = normalizeStatusForBackendValue(raw);
    if (normalized != null && String(normalized).trim()) {
      params.append("status", String(normalized).trim());
    }
  }
  if (role_id) {
    const val = typeof role_id === 'object' ? (role_id.id ?? role_id.code ?? role_id.value) : role_id;
    if (val != null && String(val).trim()) params.append('role_id', String(val).trim());
  }
  const vacancyIds = Array.isArray(vacancy_id)
    ? vacancy_id
    : vacancy_id != null && String(vacancy_id).trim()
      ? [vacancy_id]
      : [];
  for (const raw of vacancyIds) {
    const val = typeof raw === "object" ? (raw.id ?? raw) : raw;
    if (val != null && String(val).trim()) params.append("vacancy_id", String(val).trim());
  }
  if (search) params.append("search", search);
  if (category) params.append("category", String(category));
  if (is_perfect_candidate === true) {
    params.append("is_perfect_candidate", "true");
  }
  if (typeof isNotSync === "boolean") {
    params.append("is_not_sync", String(isNotSync));
  }
  if (includeArchived === true) {
    params.append("include_archived", "true");
  }
  appendListFilters(params, {
    statuses, stage, ageFrom, ageTo, salaryFrom, salaryTo, city, gender, experience,
    aiScoreMin, appliedFrom, appliedTo, sortBy, sortDir,
  });
  params.append("page", String(page + 1));
  params.append("per_page", String(per_page));

  return http.get(`/candidates?${params.toString()}`, requestOptions);
};

/** Extra list filters (правка №4/№14, Чат 1). Empty values are not sent. */
function appendListFilters(params, f = {}) {
  const put = (key, value) => {
    if (value === undefined || value === null || value === "") return;
    if (typeof value === "number" && !Number.isFinite(value)) return;
    params.append(key, String(value));
  };
  for (const status of Array.isArray(f.statuses) ? f.statuses : []) {
    const normalized = normalizeStatusForBackendValue(status);
    if (normalized != null && String(normalized).trim()) params.append("statuses", String(normalized).trim());
  }
  put("stage", f.stage);
  put("age_from", f.ageFrom);
  put("age_to", f.ageTo);
  put("salary_from", f.salaryFrom);
  put("salary_to", f.salaryTo);
  put("city", typeof f.city === "string" ? f.city.trim() : f.city);
  put("gender", f.gender);
  for (const level of Array.isArray(f.experience) ? f.experience : []) put("experience", level);
  put("ai_score_min", f.aiScoreMin);
  put("applied_from", f.appliedFrom);
  put("applied_to", f.appliedTo);
  if (f.sortBy && f.sortBy !== "activity") put("sort_by", f.sortBy);
  if (f.sortBy && f.sortBy !== "activity" && f.sortDir) put("sort_dir", f.sortDir);
  if ((!f.sortBy || f.sortBy === "activity") && f.sortDir === "asc") put("sort_dir", "asc");
}

/** Counters of the funnel tabs for the same filters as the list (правка №4/№14). */
export const getCandidateStageCounts = ({
  search = "", vacancyIds = [], isPerfect, isNotSync, ...filters
} = {}, { signal } = {}) => {
  const params = new URLSearchParams();
  if (search && String(search).trim()) params.append("search", String(search).trim());
  for (const id of Array.isArray(vacancyIds) ? vacancyIds : []) {
    if (id != null && String(id).trim()) params.append("vacancy_id", String(id).trim());
  }
  if (isPerfect === true) params.append("is_perfect_candidate", "true");
  if (isNotSync === true) params.append("is_not_sync", "true");
  const { sortBy, sortDir, stage, ...rest } = filters;
  appendListFilters(params, rest);
  const query = params.toString();
  return http.get(`/candidates/stage-counts${query ? `?${query}` : ""}`, { signal });
};

export const getHhSyncMismatches = ({ search = "", status = "", page = 1, perPage = 20, signal } = {}) => {
    const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
    if (search.trim()) params.set("search", search.trim());
    if (status) params.set("status", String(status));
    return http.get(`/candidates/hh-sync-mismatches?${params.toString()}`, { signal });
};

export const retryCandidateHhStatusSync = (candidateId) =>
    http.post(`/candidate/${candidateId}/hh-status-sync/retry`, {});

export const applyCandidateHhStatus = (candidateId, payload) =>
    http.post(`/candidate/${candidateId}/hh-status-sync/apply`, payload);

export const getCandidateHhStatusMapping = () => http.get("/candidates/hh-status-mapping");

export const importCandidateStatusFromHh = (candidateId, payload) =>
    http.post(`/candidate/${candidateId}/hh-status-sync/import`, payload);

export const getCandidate = (id) => http.get(`/candidate/${id}`);
export const createCandidate = (data) => http.post("/candidate", data);
export const updateCandidate = (id, data = {}) => {
    const payload = { ...data };
    if (payload.status != null) {
        payload.status = normalizeStatusForBackendValue(payload.status);
    }
    return http.patch(`/candidate/${id}`, payload);
};
export const deleteCandidate = (id) => http.del(`/candidate/${id}`);


export const getCandidateStatus = async (candidateId) => {
    const data = await http.get(`/candidate/${candidateId}/status`);
    return data;
};

export const createTestResult = (data) => http.post("/test/results", data)
export const createQuestionAnswer = (data) => http.post("/test/question-answers", data)
export const getTestResults = (id) => http.get(`/candidate/${id}/results`)
export const getCandidateCompletedTests = (id) => http.get(`/candidate/${id}/completed-tests`)

export const assignVacancyToCandidate = (candidateId, vacancyId, status = "неразобранный", isActive = true) =>
    http.post("/candidate/vacancy", {
        candidate_id: candidateId,
        vacancy_id: vacancyId,
        status: status,
        is_active: isActive
    });

export const changeCandidateVacancy = (candidateId, vacancyId) =>
    http.post("/candidate/vacancy/change", {
        candidate_id: Number(candidateId),
        vacancy_id: Number(vacancyId),
    });

export const sendCandidateNotification = (candidateId, vacancyId) =>
    http.post("/candidate/new-vacancy", { candidate_id: candidateId, vacancy_id: vacancyId });

export const updateCandidateStatus = (candidateId, status, statusDate = null) =>
    http.put(`/candidate/${candidateId}/status`, { status, status_date: statusDate });

export const archiveCandidate = (candidateId) =>
    http.put(`/candidate/${candidateId}/archive`);

export const blacklistCandidate = (candidateId) =>
    http.put(`/candidate/${candidateId}/blacklist`);

export const hireCandidate = (candidateId, terms) => {
    if (!terms?.date_hired) {
        throw new Error("Укажите дату выхода на работу");
    }
    if (!terms.legal_entity) throw new Error("Выберите юридическое лицо");
    return http.put(`/candidate/${candidateId}/hire`, terms);
};

/** Route hire to stage endpoint; archive-like statuses are handled by PUT /status. */
export const applyCandidateStatus = async (candidateId, status, statusDate = null) => {
    const value = normalizeStatusForBackendValue(status);
    const normalized = String(value ?? "").trim();

    if (normalized === "в штате" || normalized === "ВНР" || normalized === "Нанят") {
        throw new Error("Для добавления в сотрудники необходимо указать дату выхода на работу");
    }
    return updateCandidateStatus(candidateId, normalized, statusDate);
};

// понадобится для шага 4 в CandidateDetails
export const getCandidateById = (candidateId) =>
    http.get(`/candidate/${candidateId}`);

export const startCandidateExercise = (candidateId, instructionText, minutes) => {
    return http.post("/candidate/exercise", {
        candidate_id: candidateId,
        instruction_text: instructionText,
        minutes: minutes,
    });
}

export const sendOffer = (candidateId, offer) =>
    http.post("/candidate/offer", { candidate_id: candidateId, ...offer });

export const getCandidateOffer = (candidateId) =>
    http.get(`/candidate/${candidateId}/offer`);

export const sendInterviewInvite = (candidateId, payload = {}) =>
    http.post("/candidate/interview-invite", {
        candidate_id: Number(candidateId),
        message: payload.message,
        hr_id: payload.hr_id || null,
        interview_date: payload.interview_date || null,
        start_time: payload.start_time || null,
        end_time: payload.end_time || null,
        book_calendar: payload.book_calendar !== false,
        remind_candidate: Boolean(payload.remind_candidate),
        remind_at: payload.remind_at || null,
        reminder_message: payload.reminder_message || null,
    });

export const sendProfessionalTestToCandidate = (candidateId, testId, message = null, application = {}) =>
    http.post("/candidate/professional-test/send", {
        candidate_id: Number(candidateId),
        test_id: Number(testId),
        ...(message ? { message } : {}),
        ...(application?.applicationId ? { application_id: Number(application.applicationId) } : {}),
        ...(application?.vacancyId ? { vacancy_id: Number(application.vacancyId) } : {}),
    });

export const getArchivedCandidates = ({
    page = 1,
    perPage = 20,
    search = "",
    status = "",
    vacancyId = "",
    signal,
} = {}) => {
    const params = new URLSearchParams({
        page: String(page),
        per_page: String(perPage),
    });
    if (String(search || "").trim()) params.set("search", String(search).trim());
    if (String(status || "").trim()) params.set("status", String(status).trim());
    if (String(vacancyId || "").trim()) params.set("vacancy_id", String(vacancyId).trim());
    return http.get(`/candidates/archived?${params.toString()}`, { signal });
};

export const getBlacklistedCandidates = () => http.get("/candidates/blacklisted");

export const getCandidateActiveVacancy = (candidateId) =>
    http.get(`/candidate/${candidateId}/active-vacancy`);

export const getCandidateVacancies = (candidateId) =>
    http.get(`/candidate/${candidateId}/vacancies`);

/** Vacancy the candidate applied to (active relation, else latest linked vacancy). */
export async function loadCandidateVacancyTitle(candidateId) {
    if (candidateId == null || candidateId === "") return null;
    try {
        const rel = await getCandidateActiveVacancy(candidateId);
        const name = relationVacancyName(rel);
        if (name) return name;
    } catch (_) {
        /* 404: no active vacancy */
    }
    try {
        const data = await getCandidateVacancies(candidateId);
        const arr = Array.isArray(data) ? data : (data?.items ?? []);
        if (!arr.length) return null;
        const active = arr.find((r) => r?.is_active);
        const chosen = active || [...arr].sort((a, b) => Number(b.id || 0) - Number(a.id || 0))[0];
        return relationVacancyName(chosen);
    } catch (_) {
        return null;
    }
}
